// ============================================================
// App.js — Worktual AI Voice Platform (Redesigned)
// Dark teal/green theme
// Views: Login → Agent Console (default) | Chat
// No Infra Dashboard
// ============================================================
import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import AgentConsole from './AgentConsole';
import { CallProvider } from './CallContext';

// ── Design tokens ─────────────────────────────────────────────
const T = {
  bg:        '#0d1f1f',
  bgPanel:   '#112828',
  bgCard:    '#163333',
  bgInput:   '#0a1a1a',
  border:    '#1e3f3f',
  borderHi:  '#2a5a5a',
  teal:      '#00bfa5',
  tealDim:   '#007a6a',
  tealGlow:  '#00e5c8',
  text:      '#e0f2f0',
  textSub:   '#7ab8b0',
  textDim:   '#3d7a72',
  green:     '#00c853',
  red:       '#ff5252',
  amber:     '#ffab40',
  blue:      '#40c4ff',
};

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${T.bg}; color: ${T.text}; font-family: 'Inter', system-ui, sans-serif; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: ${T.bg}; }
  ::-webkit-scrollbar-thumb { background: ${T.tealDim}; border-radius: 3px; }
  input, textarea { font-family: inherit; }
  button { font-family: inherit; cursor: pointer; }
  .teal-btn {
    background: ${T.teal}; color: #000; border: none; border-radius: 8px;
    padding: 10px 20px; font-weight: 700; font-size: 13px;
    transition: background 0.2s;
  }
  .teal-btn:hover { background: ${T.tealGlow}; }
  .teal-btn:disabled { background: ${T.tealDim}; color: ${T.textDim}; cursor: default; }
  .ghost-btn {
    background: transparent; border: 1px solid ${T.border}; color: ${T.textSub};
    border-radius: 8px; padding: 8px 16px; font-size: 12px;
    transition: border-color 0.2s, color 0.2s;
  }
  .ghost-btn:hover { border-color: ${T.teal}; color: ${T.teal}; }
`;

function App() {
  const [token,     setToken]     = useState(localStorage.getItem('miniai_jwt'));
  const [userEmail, setUserEmail] = useState(localStorage.getItem('miniai_email') || '');
  const [view,      setView]      = useState('agent'); // 'agent' | 'chat'

  // Auth
  const [isReg,      setIsReg]      = useState(false);
  const [authEmail,  setAuthEmail]  = useState('');
  const [authPass,   setAuthPass]   = useState('');
  const [authErr,    setAuthErr]    = useState('');

  // Chat
  const [question,    setQuestion]    = useState('');
  const [convs,       setConvs]       = useState([]);
  const [activeConv,  setActiveConv]  = useState(null);
  const [messages,    setMessages]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const logout = useCallback(() => {
    localStorage.removeItem('miniai_jwt');
    localStorage.removeItem('miniai_email');
    setToken(null); setUserEmail(''); setConvs([]); setActiveConv(null);
  }, []);

  const fetchConvs = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/conversations', { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 403) return logout();
      const d = await r.json();
      setConvs(d);
      if (d.length > 0 && !activeConv) setActiveConv(d[0].id);
    } catch (_) {}
  }, [token, activeConv, logout]);

  useEffect(() => { fetchConvs(); }, [token]);

  useEffect(() => {
    if (!activeConv || !token) { setMessages([]); return; }
    fetch(`/api/conversations/${activeConv}/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setMessages).catch(() => {});
  }, [activeConv, token]);

  const handleAuth = async (e) => {
    e.preventDefault(); setAuthErr('');
    try {
      const r = await fetch(isReg ? '/api/register' : '/api/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail, password: authPass }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Auth failed');
      if (isReg) { setIsReg(false); setAuthErr('Registered! Please log in.'); }
      else {
        localStorage.setItem('miniai_jwt', d.token);
        localStorage.setItem('miniai_email', d.email);
        setToken(d.token); setUserEmail(d.email);
      }
      setAuthPass('');
    } catch (err) { setAuthErr(err.message); }
  };

  const newChat = async () => {
    const title = prompt('Chat topic:', 'New Conversation');
    if (!title) return;
    const r = await fetch('/api/conversations', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ title }),
    });
    const c = await r.json();
    setConvs(p => [c, ...p]); setActiveConv(c.id); setView('chat');
  };

  const askAI = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading || !activeConv) return;
    const q = question; setQuestion('');
    const base = [...messages, { sender: 'user', text: q }];
    setMessages(base); setLoading(true);
    try {
      const r = await fetch('/api/ask', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: q, conversationId: activeConv }),
      });
      const reader = r.body.getReader(); const dec = new TextDecoder();
      let buf = '';
      setMessages([...base, { sender: 'ai', text: '' }]); setLoading(false);
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        setMessages([...base, { sender: 'ai', text: buf }]);
      }
    } catch (_) { setMessages([...base, { sender: 'ai', text: 'Connection error.' }]); setLoading(false); }
  };

  // ── Login screen ─────────────────────────────────────────────
  if (!token) return (
    <>
      <style>{css}</style>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `radial-gradient(ellipse at 30% 50%, #0d2e2a 0%, ${T.bg} 70%)` }}>
        <div style={{ width: 360, background: T.bgPanel, border: `1px solid ${T.border}`,
                      borderRadius: 16, padding: 40 }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: T.teal,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 12px', fontSize: 24 }}>🎙</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.teal, letterSpacing: -0.5 }}>Worktual AI</div>
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>Voice Intelligence Platform</div>
          </div>

          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: T.text }}>
            {isReg ? 'Create account' : 'Sign in'}
          </div>

          {authErr && (
            <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 16, fontSize: 12,
                          background: authErr.includes('Registered') ? '#0a2e1a' : '#2e0a0a',
                          border: `1px solid ${authErr.includes('Registered') ? T.green : T.red}`,
                          color: authErr.includes('Registered') ? T.green : T.red }}>
              {authErr}
            </div>
          )}

          <form onSubmit={handleAuth}>
            {[['Email', authEmail, setAuthEmail, 'email'], ['Password', authPass, setAuthPass, 'password']].map(([label, val, set, type]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub, marginBottom: 6,
                              textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
                <input type={type} value={val} onChange={e => set(e.target.value)} required
                  style={{ width: '100%', padding: '10px 12px', background: T.bgInput,
                           border: `1px solid ${T.border}`, borderRadius: 8, color: T.text,
                           fontSize: 15, outline: 'none' }} />
              </div>
            ))}
            <button type="submit" className="teal-btn" style={{ width: '100%', marginTop: 8, padding: 12 }}>
              {isReg ? 'Create account' : 'Sign in'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: T.textSub }}>
            {isReg ? 'Already have an account? ' : "Don't have an account? "}
            <span onClick={() => { setIsReg(!isReg); setAuthErr(''); }}
              style={{ color: T.teal, cursor: 'pointer', fontWeight: 600 }}>
              {isReg ? 'Sign in' : 'Sign up'}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  // ── Main app ─────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <CallProvider token={token}>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                      background: T.bg }}>

          {/* ── Top nav bar ── */}
          <div style={{ height: 52, background: T.bgPanel, borderBottom: `1px solid ${T.border}`,
                        display: 'flex', alignItems: 'center', padding: '0 20px',
                        justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.teal,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🎙</div>
              <span style={{ fontWeight: 800, fontSize: 15, color: T.teal, letterSpacing: -0.3 }}>Worktual AI</span>
              <span style={{ fontSize: 12, color: T.textDim, background: T.bgCard, padding: '2px 8px',
                             borderRadius: 4, border: `1px solid ${T.border}` }}>Voice Platform</span>
            </div>

            {/* Nav tabs */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[['agent', '🎙 Agent Console'], ['chat', '💬 AI Chat']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600,
                           background: view === v ? T.teal : 'transparent',
                           color: view === v ? '#000' : T.textSub,
                           transition: 'all 0.15s' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* User info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, color: T.textSub }}>{userEmail}</span>
              <button onClick={logout} className="ghost-btn"
                style={{ borderColor: T.red + '60', color: T.red, padding: '5px 12px', fontSize: 12 }}>
                Sign out
              </button>
            </div>
          </div>

          {/* ── Content area ── */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>

            {/* AGENT CONSOLE VIEW */}
            {view === 'agent' && (
              <AgentConsole token={token} colors={T} />
            )}

            {/* CHAT VIEW */}
            {view === 'chat' && (
              <div style={{ display: 'flex', height: '100%' }}>
                {/* Sidebar */}
                <div style={{ width: 240, background: T.bgPanel, borderRight: `1px solid ${T.border}`,
                              display: 'flex', flexDirection: 'column', padding: 16, gap: 10 }}>
                  <button onClick={newChat} className="teal-btn" style={{ width: '100%', padding: '10px 0' }}>
                    + New Chat
                  </button>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, textTransform: 'uppercase',
                                letterSpacing: 1, marginTop: 8 }}>Conversations</div>
                  <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {convs.map(c => (
                      <div key={c.id} onClick={() => setActiveConv(c.id)}
                        style={{ padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                                 background: activeConv === c.id ? T.bgCard : 'transparent',
                                 border: `1px solid ${activeConv === c.id ? T.borderHi : 'transparent'}`,
                                 color: activeConv === c.id ? T.teal : T.textSub,
                                 whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                 transition: 'all 0.15s' }}>
                        💬 {c.title}
                      </div>
                    ))}
                    {convs.length === 0 && (
                      <div style={{ fontSize: 11, color: T.textDim, textAlign: 'center', marginTop: 20 }}>
                        No conversations yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Chat area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', display: 'flex',
                                flexDirection: 'column', gap: 16 }}>
                    {messages.length === 0 && (
                      <div style={{ textAlign: 'center', color: T.textDim, marginTop: 80 }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.textSub }}>Ask anything</div>
                        <div style={{ fontSize: 14, marginTop: 6 }}>Linux administration, server troubleshooting, DevOps</div>
                      </div>
                    )}
                    {messages.map((m, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12,
                                            flexDirection: m.sender === 'user' ? 'row-reverse' : 'row' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                      background: m.sender === 'user' ? T.teal : T.bgCard,
                                      border: `1px solid ${m.sender === 'user' ? T.teal : T.border}`,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 14, color: m.sender === 'user' ? '#000' : T.teal }}>
                          {m.sender === 'user' ? '👤' : '🤖'}
                        </div>
                        <div style={{ maxWidth: '72%', padding: '12px 16px', borderRadius: 12,
                                      background: m.sender === 'user' ? T.tealDim : T.bgCard,
                                      border: `1px solid ${m.sender === 'user' ? T.teal + '60' : T.border}`,
                                      fontSize: 15, lineHeight: 1.7, color: T.text }}>
                          <ReactMarkdown>{m.text}</ReactMarkdown>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: T.textSub, fontSize: 12 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.teal,
                                      animation: 'pulse 1s infinite' }} />
                        Thinking...
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  <div style={{ padding: '16px 32px', borderTop: `1px solid ${T.border}` }}>
                    <form onSubmit={askAI} style={{ display: 'flex', gap: 10 }}>
                      <textarea value={question} onChange={e => setQuestion(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), askAI(e))}
                        placeholder={activeConv ? 'Ask about Linux, servers, DevOps...' : 'Select a conversation first'}
                        disabled={!activeConv}
                        rows={1}
                        style={{ flex: 1, padding: '10px 14px', background: T.bgInput,
                                 border: `1px solid ${T.border}`, borderRadius: 10, color: T.text,
                                 fontSize: 15, outline: 'none', resize: 'none',
                                 transition: 'border-color 0.2s' }} />
                      <button type="submit" className="teal-btn"
                        disabled={loading || !question.trim() || !activeConv}>
                        Send
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CallProvider>
    </>
  );
}

export default App;
